import React, { useState, useEffect } from "react";
import {
  Grid,
  Paper,
  Box,
  Typography,
  TextField,
  Button,
  Avatar,
  List,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  InputAdornment,
  Divider,
  Container,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import PersonIcon from "@mui/icons-material/Person";
import LinkIcon from "@mui/icons-material/Link";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import LockIcon from "@mui/icons-material/Lock";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import HelpIcon from "@mui/icons-material/Help";
import ShieldIcon from "@mui/icons-material/Shield";
import SyncIcon from "@mui/icons-material/Sync";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import VerifiedIcon from "@mui/icons-material/Verified";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { useSelector, useDispatch } from "react-redux";
import SecurityDetails from "./SecurityDetails";
import { useLocation, useNavigate } from "react-router-dom";
import RoleManagementComponent from "./RoleManagementComponent";
import AddUserForm from "./AddUserForm";

function ProfileSettingsComponent() {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { userInfo } = useSelector((state) => state.user || {});
  const { userPermissions } = useSelector((state) => state.userStatus || {});
  
  // Get the tab parameter from the URL
  const pathname = location.pathname;
  const tabFromUrl = pathname.split('/')[2];
  
  // Map URL parameters to menu labels
  const urlToMenuMap = {
    'account': 'Account',
    'Role-Management': 'Role Management',
    'adduser': 'Add Users',
    'privacy': 'Privacy',
    'security': 'Security',
    'appearance': 'Appearance',
    'support': 'Support'
  };
  
  // Initialize selectedMenu with the mapped URL tab parameter or default to 'Account'
  const [activeSection, setActiveSection] = useState(urlToMenuMap[tabFromUrl] || 'Account');
  
  const [personalInfo, setPersonalInfo] = useState({
    firstName: "",
    lastName: "",
    email: "",
    dateOfBirth: "",
  });

  useEffect(() => {
    // Populate with user data when available
    if (userInfo) {
      setPersonalInfo({
        firstName: userInfo.firstName || "",
        lastName: userInfo.lastName || "",
        email: userInfo.email || "",
        dateOfBirth: userInfo.dateOfBirth || "",
      });
    }
  }, [userInfo]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPersonalInfo({
      ...personalInfo,
      [name]: value,
    });
  };

  const handleSaveChanges = () => {
    // Implement save functionality here
  };
  
  const hasPermission = (permissionName) => {
    return userPermissions?.some(permission => 
      permission?.apiName === permissionName && permission?.canAccess
    );
  };
  
  const handleMenuClick = (label) => {
    setActiveSection(label);
    // Update URL when menu item is clicked
    const urlParam = Object.entries(urlToMenuMap).find(([key, value]) => value === label)?.[0];
    if (urlParam) {
      navigate(`/profile-settings/${urlParam}`);
    }
  };

  const menuItems = [
    { icon: <LinkIcon />, label: 'Role Management', description: 'Manage accounts', permission: 'addAccountInfo' },
    { icon: <GroupAddIcon />, label: 'Add Managers', description: 'Add Managers', permission: 'createUser' },
    { icon: <LockIcon />, label: 'Privacy', description: 'Update password' },
    { icon: <ShieldIcon />, label: 'Security', description: 'Enable two-factor authentication' },
    { icon: <DarkModeIcon />, label: 'Appearance', description: 'Light or Dark mode' },
    { icon: <HelpIcon />, label: 'Support', description: 'Have questions? Reach out' },
  ];

  const renderContent = () => {
    switch (activeSection) {
      case "Account":
        return (
          <Paper elevation={1} sx={{ p: 3, minHeight: '500px' }}>
            <Typography variant="h6" sx={{ mb: 3 }}>Personal Details</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  First Name
                </Typography>
                <TextField
                  fullWidth
                  name="firstName"
                  value={personalInfo.firstName}
                  onChange={handleInputChange}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Last Name
                </Typography>
                <TextField
                  fullWidth
                  name="lastName"
                  value={personalInfo.lastName}
                  onChange={handleInputChange}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Email Address
                </Typography>
                <TextField
                  fullWidth
                  name="email"
                  value={personalInfo.email}
                  onChange={handleInputChange}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Date of Birth
                </Typography>
                <TextField
                  fullWidth
                  name="dateOfBirth"
                  value={personalInfo.dateOfBirth}
                  onChange={handleInputChange}
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <CalendarTodayIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
            <Box sx={{ display: "flex", mt: 4 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSaveChanges}
                sx={{ mr: 2 }}
              >
                Save changes
              </Button>
              <Button variant="outlined">
                Cancel
              </Button>
            </Box>
          </Paper>
        );
      case "Role Management":
        return <RoleManagementComponent />;
      case "Add Managers":
        return <AddUserForm />;
      case "Privacy":
        return (
          <Paper elevation={1} sx={{ p: 3, minHeight: '500px' }}>
            <Typography variant="h6">Privacy Settings</Typography>
            <Typography variant="body1" sx={{ mt: 2 }}>
              Update your password and privacy settings here.
            </Typography>
          </Paper>
        );
      case "Security":
        return <SecurityDetails />;
      case "Appearance":
        return (
          <Paper elevation={1} sx={{ p: 3, minHeight: '500px' }}>
            <Typography variant="h6">Appearance</Typography>
            <Typography variant="body1" sx={{ mt: 2 }}>
              Choose between light and dark mode.
            </Typography>
          </Paper>
        );
      case "Support":
        return (
          <Paper elevation={1} sx={{ p: 3, minHeight: '500px' }}>
            <Typography variant="h6">Support</Typography>
            <Typography variant="body1" sx={{ mt: 2 }}>
              Get help and support here.
            </Typography>
          </Paper>
        );
      default:
        return null;
    }
  };

  return (
    <Container maxWidth="lg">
      <Typography variant="h4" sx={{ fontWeight: 600, color: "#18275A", mt: 10 }}>
        Profile
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 4 }}>
        Lorem ipsum dolor sit amet consectetur. Accumsan eget est.
      </Typography>
      
      <Box sx={{ display: 'flex', gap: 3 }}>
        <Box sx={{ width: '40%' }}>
          <Paper elevation={1} sx={{ p: 3, height: "100%" }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <Avatar
                sx={{
                  width: 64,
                  height: 64,
                  bgcolor: "#e0e0e0",
                  fontSize: "1.5rem",
                  mr: 2,
                }}
              >
                {personalInfo.firstName ? personalInfo.firstName.charAt(0) : "A"}
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ 
                  display: "flex", 
                  alignItems: "center", 
                  fontWeight: 500,
                  color: "#18275A"
                }}>
                  Admin
                  <VerifiedIcon color="primary" sx={{ ml: 0.5, width: 20, height: 20 }} />
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            <List component="nav" sx={{ width: '100%' }}>
              {menuItems.map((item) => {
                // Check if user has permission for this menu item
                const userType = localStorage.getItem('type');
                if (userType === 'SUB_INVESTOR' && item.permission && !hasPermission(item.permission)) {
                  return null;
                }
                
                return (
                  <ListItemButton 
                    key={item.label}
                    selected={activeSection === item.label}
                    onClick={() => handleMenuClick(item.label)}
                    sx={{ 
                      borderRadius: 1,
                      mb: 1,
                      '&.Mui-selected': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      },
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.08),
                      }
                    }}
                  >
                    <ListItemIcon sx={{ color: 'text.secondary' }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText 
                      primary={item.label} 
                      secondary={item.description}
                      primaryTypographyProps={{ 
                        variant: 'subtitle1', 
                        sx: { fontWeight: 500, color: '#18275A' } 
                      }}
                      secondaryTypographyProps={{ 
                        variant: 'body2', 
                        sx: { color: 'text.secondary', fontSize: '0.75rem' } 
                      }}
                    />
                    <ChevronRightIcon sx={{ color: 'text.secondary' }} />
                  </ListItemButton>
                );
              })}
            </List>
          </Paper>
        </Box>

        <Box sx={{ width: '60%' }}>
          {renderContent()}
        </Box>
      </Box>
    </Container>
  );
}

export default ProfileSettingsComponent; 